import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { presupuestosApi, emailTrackingApi, exportApi } from '../lib/api';
import { formatCurrency, formatDate, formatDateTime, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  Copy, 
  Trash2, 
  Eye,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  FileText,
  RefreshCw,
  History,
  Paperclip,
  Upload,
  Image,
  Film,
  Pencil,
  File,
  Mail,
  MailOpen
} from 'lucide-react';

export default function DetallePresupuestoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [emailTracking, setEmailTracking] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadPresupuesto = async () => {
      try {
        const [presupuestoRes, adjuntosRes, trackingRes] = await Promise.all([
          presupuestosApi.getOne(id),
          presupuestosApi.getAdjuntos(id).catch(() => ({ data: [] })),
          emailTrackingApi.getTracking(id).catch(() => ({ data: null }))
        ]);
        setPresupuesto(presupuestoRes.data);
        setAdjuntos(adjuntosRes.data || []);
        setEmailTracking(trackingRes.data);
      } catch (error) {
        console.error('Error loading presupuesto:', error);
        toast.error('Error al cargar el presupuesto');
        navigate('/presupuestos');
      } finally {
        setLoading(false);
      }
    };
    loadPresupuesto();
  }, [id, navigate]);

  const handleExportItemsCsv = async () => {
    try {
      const response = await exportApi.itemsPresupuesto(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `items_${presupuesto.numero.replace(/\//g, '-')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Items exportados');
    } catch (error) {
      toast.error('Error al exportar items');
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await presupuestosApi.exportPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Presupuesto_${presupuesto.numero.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleEnviar = async () => {
    try {
      const response = await presupuestosApi.enviar(id);
      toast.success('Presupuesto enviado correctamente');
      setPresupuesto({ ...presupuesto, estado: 'pendiente', enviado_at: new Date().toISOString() });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
  };

  const handleDuplicar = async () => {
    try {
      const response = await presupuestosApi.duplicar(id);
      toast.success('Presupuesto duplicado');
      navigate(`/presupuestos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar');
    }
  };

  const handleCrearRevision = async () => {
    if (!window.confirm('¿Crear una nueva revisión de este presupuesto? El presupuesto actual se marcará como "revisado" y se creará una nueva versión editable.')) return;
    
    setCreatingRevision(true);
    try {
      const response = await presupuestosApi.crearRevision(id);
      toast.success(`Revisión ${response.data.revision} creada`);
      navigate(`/presupuestos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear revisión');
    } finally {
      setCreatingRevision(false);
    }
  };

  const handleEliminar = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este presupuesto?')) return;
    
    try {
      await presupuestosApi.delete(id);
      toast.success('Presupuesto eliminado');
      navigate('/presupuestos');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleUploadAdjunto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 50MB');
      return;
    }
    
    setUploadingFile(true);
    try {
      const response = await presupuestosApi.uploadAdjunto(id, file);
      setAdjuntos([...adjuntos, response.data]);
      toast.success('Archivo adjuntado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAdjunto = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo adjunto?')) return;
    
    try {
      await presupuestosApi.deleteAdjunto(id, adjuntoId);
      setAdjuntos(adjuntos.filter(a => a.id !== adjuntoId));
      toast.success('Archivo eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getFileIcon = (tipo) => {
    if (tipo?.startsWith('image/')) return <Image className="w-5 h-5 text-emerald-500" />;
    if (tipo?.startsWith('video/')) return <Film className="w-5 h-5 text-violet-500" />;
    if (tipo === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getEventIcon = (tipo) => {
    switch (tipo) {
      case 'aprobado':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'rechazado':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'visto':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'enviado':
        return <Send className="w-4 h-4 text-violet-500" />;
      case 'revision':
        return <RefreshCw className="w-4 h-4 text-amber-500" />;
      case 'recordatorio':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'adjunto':
        return <Paperclip className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!presupuesto) return null;

  const canEdit = hasPermission(['OWNER', 'MANAGER']);
  const publicUrl = `${window.location.origin}/p/${presupuesto.enlace_publico}`;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4 md:space-y-6 animate-fadeIn" data-testid="detalle-presupuesto-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/presupuestos')} className="self-start">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {presupuesto.numero}
                </h1>
                <span className={`px-2 md:px-3 py-1 text-xs md:text-sm font-medium rounded border ${getStatusColor(presupuesto.estado)}`}>
                  {getStatusLabel(presupuesto.estado)}
                </span>
                {presupuesto.revision > 1 && (
                  <Badge variant="outline" className="text-amber-600 border-amber-200">
                    <History className="w-3 h-3 mr-1" />
                    Rev. {presupuesto.revision}
                  </Badge>
                )}
              </div>
              <p className="text-slate-500 mt-1 text-sm">
                Creado el {formatDate(presupuesto.created_at)}
              </p>
            </div>
          </div>
          
          {canEdit && (
            <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-end mt-4 md:mt-0">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                data-testid="export-pdf-btn"
                className="px-2 md:px-3"
              >
                <Download className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">{exportingPdf ? 'Generando...' : 'PDF'}</span>
              </Button>
              {/* Botón Editar para borradores y pendientes */}
              {(presupuesto.estado === 'borrador' || presupuesto.estado === 'pendiente') && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/presupuestos/${presupuesto.id}/editar`)}
                  data-testid="edit-presupuesto-btn"
                  className="px-2 md:px-3"
                >
                  <Pencil className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Editar</span>
                </Button>
              )}
              {presupuesto.estado === 'borrador' && (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 px-2 md:px-3" onClick={handleEnviar}>
                  <Send className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Enviar</span>
                </Button>
              )}
              {presupuesto.estado !== 'aprobado' && presupuesto.estado !== 'revisado' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-amber-600 hover:bg-amber-50 px-2 md:px-3"
                  onClick={handleCrearRevision}
                  disabled={creatingRevision}
                  data-testid="crear-revision-btn"
                >
                  <RefreshCw className={`w-4 h-4 md:mr-2 ${creatingRevision ? 'animate-spin' : ''}`} />
                  <span className="hidden md:inline">{creatingRevision ? 'Creando...' : 'Revisión'}</span>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleDuplicar} className="px-2 md:px-3">
                <Copy className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Duplicar</span>
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 px-2 md:px-3" onClick={handleEliminar}>
                <Trash2 className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Eliminar</span>
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quote Preview */}
            <Card>
              <CardContent className="p-0">
                <div className="quote-preview border-0 shadow-none">
                  <div className="quote-header">
                    <div className="flex justify-between items-start">
                      <div>
                        {/* Logo grande O nombre comercial grande - no ambos */}
                        {(presupuesto.empresa?.logo || presupuesto.empresa?.logo_url) ? (
                          <img 
                            src={presupuesto.empresa.logo_url || `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logos/${presupuesto.empresa.logo}`}
                            alt={presupuesto.empresa?.nombre_comercial || 'Logo'}
                            className="h-16 w-auto object-contain mb-3"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <h3 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {presupuesto.empresa?.nombre_comercial || 'Tu Empresa'}
                          </h3>
                        )}
                        {presupuesto.empresa?.direccion && (
                          <p className="text-slate-500 text-sm mt-1">{presupuesto.empresa.direccion}</p>
                        )}
                        {presupuesto.empresa?.cif_nif && (
                          <p className="text-slate-500 text-sm">CIF/NIF: {presupuesto.empresa.cif_nif}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Presupuesto</p>
                        <p className="font-mono text-slate-900">{presupuesto.numero}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {formatDate(presupuesto.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-500">Para:</p>
                      <p className="font-medium text-slate-900">{presupuesto.cliente?.nombre}</p>
                      {presupuesto.cliente?.empresa_cliente && (
                        <p className="text-slate-600">{presupuesto.cliente.empresa_cliente}</p>
                      )}
                      {presupuesto.cliente?.cif_nif && (
                        <p className="text-slate-600 text-sm">CIF/NIF: {presupuesto.cliente.cif_nif}</p>
                      )}
                      <p className="text-slate-600">{presupuesto.cliente?.email}</p>
                      {presupuesto.cliente?.telefono && (
                        <p className="text-slate-600 text-sm">Tel: {presupuesto.cliente.telefono}</p>
                      )}
                      {/* Dirección completa del cliente */}
                      {(presupuesto.cliente?.direccion || presupuesto.cliente?.ciudad || presupuesto.cliente?.codigo_postal) && (
                        <p className="text-slate-500 text-sm mt-1">
                          {[
                            presupuesto.cliente?.direccion,
                            [presupuesto.cliente?.codigo_postal, presupuesto.cliente?.ciudad].filter(Boolean).join(' '),
                            presupuesto.cliente?.provincia
                          ].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="quote-items overflow-x-auto">
                    <table className="w-full min-w-[450px]">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 text-slate-500 text-xs md:text-sm font-medium">Concepto</th>
                          <th className="text-center py-2 text-slate-500 text-xs md:text-sm font-medium w-14">Cant.</th>
                          <th className="text-right py-2 text-slate-500 text-xs md:text-sm font-medium w-20">Precio</th>
                          <th className="text-right py-2 text-slate-500 text-xs md:text-sm font-medium w-16">Dto.</th>
                          <th className="text-right py-2 text-slate-500 text-xs md:text-sm font-medium w-20">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {presupuesto.items?.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-2 md:py-3 pr-2 text-sm">{item.descripcion}</td>
                            <td className="py-2 md:py-3 text-center text-sm">{item.cantidad}</td>
                            <td className="py-2 md:py-3 text-right text-sm">{formatCurrency(item.precio_unitario)}</td>
                            <td className="py-2 md:py-3 text-right text-emerald-600 text-sm">
                              {item.descuento_aplicado > 0 
                                ? `-${formatCurrency(item.descuento_aplicado)}`
                                : ''
                              }
                            </td>
                            <td className="py-2 md:py-3 text-right font-medium text-sm">{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="quote-footer">
                    <div className="flex justify-end">
                      <div className="w-full md:w-64 space-y-2">
                        <div className="flex justify-between text-sm md:text-base">
                          <span className="text-slate-500">Subtotal</span>
                          <span>{formatCurrency(presupuesto.subtotal)}</span>
                        </div>
                        {presupuesto.total_descuentos > 0 && (
                          <div className="flex justify-between text-emerald-600">
                            <span>Descuentos</span>
                            <span>-{formatCurrency(presupuesto.total_descuentos)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm md:text-base">
                          <span className="text-slate-500">IVA (21%)</span>
                          <span>{formatCurrency(presupuesto.iva)}</span>
                        </div>
                        <div className="flex justify-between text-base md:text-lg font-bold pt-2 border-t">
                          <span>Total</span>
                          <span>{formatCurrency(presupuesto.total)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 md:mt-6 pt-4 border-t border-slate-200 text-xs md:text-sm text-slate-500">
                      <p><strong>Validez:</strong> {presupuesto.validez_dias} días</p>
                      <p><strong>Forma de pago:</strong> {presupuesto.forma_pago}</p>
                      {presupuesto.notas && <p className="mt-2"><strong>Notas:</strong> {presupuesto.notas}</p>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Adjuntos */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Paperclip className="w-4 h-4" />
                    Archivos Adjuntos
                    {adjuntos.length > 0 && (
                      <Badge variant="secondary">{adjuntos.length}</Badge>
                    )}
                  </CardTitle>
                  {canEdit && (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleUploadAdjunto}
                        className="hidden"
                        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                      >
                        <Upload className={`w-4 h-4 mr-1 ${uploadingFile ? 'animate-pulse' : ''}`} />
                        {uploadingFile ? 'Subiendo...' : 'Adjuntar'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {adjuntos.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <Paperclip className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No hay archivos adjuntos</p>
                    <p className="text-xs">Puedes adjuntar imágenes, PDFs, videos y más</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adjuntos.map(adjunto => {
                      const isImage = adjunto.tipo?.startsWith('image/');
                      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/adjuntos/${adjunto.nombre_archivo}`;
                      
                      return (
                        <div 
                          key={adjunto.id}
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          {isImage ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                              <img 
                                src={fileUrl} 
                                alt={adjunto.nombre_original}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              {getFileIcon(adjunto.tipo)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{adjunto.nombre_original}</p>
                            <p className="text-xs text-slate-500">{formatFileSize(adjunto.tamaño)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => window.open(fileUrl, '_blank')}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {canEdit && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteAdjunto(adjunto.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Public Link */}
            {presupuesto.estado !== 'borrador' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Enlace Público
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm font-mono break-all text-slate-600">
                    {publicUrl}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-3"
                    onClick={() => window.open(publicUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir enlace
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Vistas</span>
                  <span className="font-medium">{presupuesto.vistas || 0}</span>
                </div>
                {presupuesto.enviado_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Enviado</span>
                    <span className="text-sm">{formatDateTime(presupuesto.enviado_at)}</span>
                  </div>
                )}
                {presupuesto.visto_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Primera vista</span>
                    <span className="text-sm">{formatDateTime(presupuesto.visto_at)}</span>
                  </div>
                )}
                {presupuesto.respondido_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Respondido</span>
                    <span className="text-sm">{formatDateTime(presupuesto.respondido_at)}</span>
                  </div>
                )}
                
                {/* Email Tracking */}
                {emailTracking && emailTracking.total_emails > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <h4 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Tracking
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Emails enviados</span>
                        <span>{emailTracking.total_emails}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Emails abiertos</span>
                        <span className="flex items-center gap-1">
                          {emailTracking.opened_emails > 0 ? (
                            <MailOpen className="w-3 h-3 text-green-500" />
                          ) : (
                            <Mail className="w-3 h-3 text-slate-400" />
                          )}
                          {emailTracking.opened_emails}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Total aperturas</span>
                        <span>{emailTracking.total_opens}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Comment */}
            {presupuesto.comentario_cliente && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Comentario del Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 italic">"{presupuesto.comentario_cliente}"</p>
                </CardContent>
              </Card>
            )}

            {/* Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Historial de Eventos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {presupuesto.eventos?.map((evento, index) => (
                    <div key={evento.id || index} className="flex gap-3">
                      <div className="mt-0.5">{getEventIcon(evento.tipo)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900">{evento.descripcion}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {formatDateTime(evento.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
